/**
 * CAMBIO DE CONTRASEÑA
 *
 * Pantalla aparte, no una pestaña dentro del Shell: cuando
 * `requiereCambioPwd` está activo, es literalmente lo único que la cuenta
 * puede hacer además de cerrar sesión (la guardia real está en el
 * backend — ver `autenticar()` en servidor.ts —, esto es sólo la UI que
 * no ofrece caminos que van a fallar). Mismo estilo visual que
 * `/entrar`: es la misma familia de pantalla, «antes de entrar a la
 * aplicación».
 *
 * Esta misma pantalla sirve también para un cambio voluntario (enlace en
 * el pie de la barra lateral): el formulario es idéntico, sólo cambia el
 * texto de cabecera y el destino al terminar.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { FalloApi } from '../api.ts';
import { Marca } from '../componentes/shell.tsx';
import { useBranding, useSesion } from '../contexto.tsx';

export function PaginaCambiarPassword(): ReactNode {
  const b = useBranding();
  const { usuario, cambiarPassword, salir } = useSesion();
  const navegar = useNavigate();

  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNueva, setPasswordNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  const obligatorio = usuario?.requiereCambioPwd === true;

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setCamposMal({});

    if (passwordNueva !== confirmacion) {
      setCamposMal({ confirmacion: 'No coincide con la contraseña nueva.' });
      return;
    }

    setEnviando(true);
    try {
      await cambiarPassword(passwordActual, passwordNueva);
      navegar('/clientes', { replace: true });
    } catch (e2) {
      if (e2 instanceof FalloApi) {
        setError(e2.message);
        setCamposMal(e2.erroresDeCampo ?? {});
      } else {
        setError('No se ha podido cambiar la contraseña.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
          <div className="nombre">{b.nombre}</div>
        </div>

        <h2 style={{ marginTop: 0 }}>Cambiar contraseña</h2>
        {obligatorio && (
          <p className="tenue" style={{ marginTop: -8, marginBottom: 16 }}>
            Tu contraseña es temporal. Elige una nueva para poder continuar.
          </p>
        )}

        <form
          onSubmit={(e) => {
            void enviar(e);
          }}
          noValidate
        >
          {error !== null && (
            <div className="nota error" role="alert">
              {error}
            </div>
          )}

          <div className="campo">
            <label htmlFor="password-actual">Contraseña actual</label>
            <input
              id="password-actual"
              name="password-actual"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={passwordActual}
              onChange={(e) => setPasswordActual(e.target.value)}
              aria-invalid={camposMal['passwordActual'] !== undefined}
            />
            {camposMal['passwordActual'] !== undefined && (
              <div className="error-campo">{camposMal['passwordActual']}</div>
            )}
          </div>

          <div className="campo">
            <label htmlFor="password-nueva">Contraseña nueva</label>
            <input
              id="password-nueva"
              name="password-nueva"
              type="password"
              autoComplete="new-password"
              value={passwordNueva}
              onChange={(e) => setPasswordNueva(e.target.value)}
              aria-invalid={camposMal['passwordNueva'] !== undefined}
            />
            {camposMal['passwordNueva'] !== undefined && (
              <div className="error-campo">{camposMal['passwordNueva']}</div>
            )}
          </div>

          <div className="campo">
            <label htmlFor="password-confirmacion">Repite la contraseña nueva</label>
            <input
              id="password-confirmacion"
              name="password-confirmacion"
              type="password"
              autoComplete="new-password"
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              aria-invalid={camposMal['confirmacion'] !== undefined}
            />
            {camposMal['confirmacion'] !== undefined && (
              <div className="error-campo">{camposMal['confirmacion']}</div>
            )}
          </div>

          <button type="submit" className="principal ancho" disabled={enviando}>
            {enviando ? 'Cambiando…' : 'Cambiar contraseña'}
          </button>
        </form>

        {obligatorio && (
          <div className="login-pie">
            <button
              type="button"
              className="enlace"
              onClick={() => {
                void salir().then(() => navegar('/entrar'));
              }}
            >
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
